/**
 * Content language for course topics.
 *
 * Courses ship bilingual: every lesson exists twice as two separate topics,
 * distinguished only by a trailing suffix on the topic name —
 * "3.4. Alcohol: How Much Is Too Much? (Hindi)". There is no `language` column;
 * `@@unique([courseId, name])` on Topic means the two tracks *must* carry
 * different names, so the suffix is structural rather than cosmetic.
 *
 * Verified against the data: 86 topics, 43 (English) / 43 (Hindi), zero
 * unmarked, and the marker is always terminal. Material titles carry no marker,
 * which is why this filters topics rather than materials.
 */

import { COURSE_LANGUAGE_KEY } from "./localStoragekeys";

export const ENGLISH = "ENGLISH";
export const HINDI = "HINDI";

export const LANGUAGES = [
  { value: ENGLISH, label: "English" },
  { value: HINDI, label: "Hindi" },
];

// Anchored to the end on purpose. A loose `includes("Hindi")` would misfire on
// a topic legitimately *about* Hindi; every real marker is a trailing suffix.
const LANGUAGE_SUFFIX = /\s*\((english|hindi)\)\s*$/i;

/**
 * @returns {"ENGLISH" | "HINDI" | null} null when the topic carries no marker —
 *   that is "unknown", not a default, and callers should show such topics
 *   whatever the selected language.
 */
export function detectTopicLanguage(topic) {
  const name = typeof topic === "string" ? topic : topic?.name;
  if (typeof name !== "string") return null;

  const match = name.match(LANGUAGE_SUFFIX);
  if (!match) return null;

  return match[1].toLowerCase() === "hindi" ? HINDI : ENGLISH;
}

/** The topic name without its language suffix, for display. */
export function stripLanguageSuffix(name) {
  if (typeof name !== "string") return name;
  return name.replace(LANGUAGE_SUFFIX, "");
}

/**
 * Which languages a course actually has, in LANGUAGES order. Used to hide the
 * picker entirely on single-language courses.
 */
export function getAvailableLanguages(topics) {
  const present = new Set(
    (Array.isArray(topics) ? topics : [])
      .map(detectTopicLanguage)
      .filter(Boolean)
  );

  return LANGUAGES.filter((language) => present.has(language.value));
}

// ============================================================================
// Session preference
// ============================================================================

/**
 * The learner's chosen track. Session-scoped: picked once per tab, not
 * remembered across visits.
 *
 * Storage access *throws* in Safari private mode rather than returning null,
 * so both helpers are defensive — a browser that refuses to store the
 * preference should still get a working page.
 */
export function readCourseLanguage() {
  try {
    return sessionStorage.getItem(COURSE_LANGUAGE_KEY) || ENGLISH;
  } catch {
    return ENGLISH;
  }
}

/** Best-effort persist; filtering still works for this page view if it fails. */
export function writeCourseLanguage(language) {
  try {
    sessionStorage.setItem(COURSE_LANGUAGE_KEY, language);
  } catch {
    // ignored on purpose
  }
}
